<template>
	<div
		:class="[
			'flex items-center px-6 duration-300 lg:px-16',
			y ? 'bg-white/90 py-3.5 shadow-md backdrop-blur-md' : 'bg-white/0 py-5'
		]"
	>
		<NuxtLink :to="$routeUrl.HomeUrl()">
			<img src="/img/app_logo.png" alt="" :class="['w-auto duration-300', y ? 'h-11' : 'h-12']" />
		</NuxtLink>
		<div class="flex-grow-[1]"></div>
		<div class="hidden items-center px-6 lg:flex">
			<template v-for="(item, i) in menuItems" :key="item.id">
				<NuxtLink
					:to="item.link"
					:class="[
						'duration-300',
						selected == item.id
							? 'font-bold text-primary'
							: 'font-semibold text-[#382C5C] hover:text-primary'
					]"
				>
					{{ item.title }}
				</NuxtLink>
				<span
					v-if="menuItems.length - 1 != i"
					class="mx-3 h-1 w-1 flex-shrink-0 rounded-full bg-[#382C5C]/50"
				></span>
			</template>
		</div>
		<div class="flex-grow-[3]"></div>
		<BaseLanguageSelector size="md" class="mx-6 hidden lg:flex" />
		<span
			:class="[
				'text-neutral-900 ms-4 flex cursor-pointer duration-300 lg:hidden rtl:-scale-x-100',
				'flex lg:hidden',
				y ? 'h-11 w-11' : 'h-12 w-12'
			]"
		>
			<Icon name="gg:menu-right" class="h-full w-full" @click="state.isMenuOpen = !state.isMenuOpen" />
		</span>

		<NuxtLink
			v-if="!user?.id"
			:to="$routeUrl.LoginUrl()"
			class="ripple-bg-primary text-white hidden rounded-md px-8 py-3 text-sm font-bold lg:flex"
		>
			{{ $t("login_register") }}
		</NuxtLink>
		<div v-else class="relative hidden flex-shrink-0 lg:inline-block" @click="(e) => e.stopPropagation()">
			<div
				class="h-11 w-11 cursor-pointer overflow-hidden rounded-full"
				@click="state.isUserMenuVisible = !state.isUserMenuVisible"
			>
				<template v-if="!user?.image_id">
					<Avatar
						variant="beam"
						:colors="avatarColors"
						:name="user.id + ''"
						class="h-full w-full cursor-pointer rounded-full border-2"
					/>
				</template>
				<img
					v-else
					class="h-full w-full object-cover"
					:src="$config.public.baseUrl + user.image_thumb1"
					alt=""
				/>
			</div>

			<div
				v-if="state.isUserMenuVisible"
				class="bg-white absolute end-0 z-10 mt-1 flex w-52 flex-col overflow-hidden rounded-lg border border-primary border-opacity-60 shadow-xl"
			>
				<MainUserMenuBox
					@update:loading="(v) => emit('update:loading', v)"
					@close="state.isUserMenuVisible = false"
				/>
			</div>
		</div>
		<Teleport to="body">
			<ClientOnly>
				<div
					:class="[
						'fixed z-[5] bg-slate-950/50 duration-500 top-safe right-safe bottom-safe left-safe',
						state.isMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
					]"
					@click="state.isMenuOpen = false"
				></div>
				<div
					class="pointer-events-none fixed z-[6] overflow-hidden overscroll-contain top-safe right-safe bottom-safe left-safe"
				>
					<div
						:class="[
							'bg-white absolute end-0 top-0 flex w-full max-w-[300px] flex-col shadow-2xl duration-500 h-dvh-safe',
							state.isMenuOpen
								? 'pointer-events-auto translate-x-0'
								: 'pointer-events-none opacity-0 ltr:translate-x-full rtl:-translate-x-full'
						]"
					>
						<div
							:class="[
								'sticky flex flex-shrink-0 items-center px-6 shadow-md duration-500 top-safe',
								y ? 'py-3.5' : 'py-5'
							]"
						>
							<div class="flex-grow"></div>
							<Icon
								name="mdi:close"
								:class="[
									'text-neutral-900 ms-4 cursor-pointer duration-500 rtl:-scale-x-100',
									'flex lg:hidden',
									y ? 'h-11 w-11' : 'h-12 w-12'
								]"
								@click="state.isMenuOpen = false"
							/>
						</div>
						<div class="flex flex-grow flex-col overflow-y-auto pt-1">
							<div class="flex flex-col">
								<NuxtLink
									v-for="item in menuItems"
									:key="item.id"
									:to="item.link"
									:class="[
										'ripple-bg-white border-slate-300 border-b px-5 py-4 duration-300',
										selected == item.id
											? 'font-bold text-primary'
											: 'font-semibold text-[#382C5C] hover:text-primary'
									]"
								>
									{{ item.title }}
								</NuxtLink>
							</div>
							<NuxtLink
								v-if="!user?.id"
								:to="$routeUrl.LoginUrl()"
								class="ripple-bg-primary text-white mx-5 mt-8 rounded-md px-8 py-4 text-center text-sm font-bold"
							>
								{{ $t("login_register") }}
							</NuxtLink>
							<div v-else class="mt-8 flex flex-col px-5">
								<MainUserMenuBox
									class="border-slate-300 rounded-xl border"
									@update:loading="(v) => emit('update:loading', v)"
									@close="state.isMenuOpen = false"
								/>
							</div>
							<div class="flex-grow"></div>
							<BaseLanguageSelector class="mb-6 mt-12 self-center" />
						</div>
					</div>
				</div>
			</ClientOnly>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import Avatar from "vue-boring-avatars"

const app = useNuxtApp()
const userStore = useUserStore()
const mounted = useMounted()

const emit = defineEmits<{
	"update:loading": [boolean]
}>()

defineProps<{
	selected?: string
	noCompanyBranding?: boolean
}>()

const state = reactive({
	isUserMenuVisible: false,
	isMenuOpen: false
})

const { y: _y } = useWindowScroll()
const y = computed(() => (mounted.value ? _y.value : 0))

const avatarColors = computed(() => {
	return ["#CBDAD5", "#89A7B1", "#566981", "#3A415A", "#34344E"]
})

const user = computed(() => userStore.user)

const menuItems = computed(() => [
	{
		id: "home",
		title: app.$i18n.t("home"),
		link: app.$routeUrl.HomeUrl()
	},
	{
		id: "about-us",
		title: app.$i18n.t("about_us"),
		link: app.$routeUrl.AboutUsUrl()
	},
	{
		id: "contact-us",
		title: app.$i18n.t("contact_us"),
		link: app.$routeUrl.ContactUsUrl()
	},
	{
		id: "blog",
		title: app.$i18n.t("blog"),
		link: app.$routeUrl.BlogUrl()
	},
	{
		id: "help",
		title: app.$i18n.t("help"),
		link: app.$routeUrl.HelpUrl()
	}
])

const clickOutside = () => {
	state.isUserMenuVisible = false
}

onMounted(() => {
	document.addEventListener("click", clickOutside)
})

onBeforeUnmount(() => {
	document.removeEventListener("click", clickOutside)
})
</script>
